import type { Logger } from "@expert/logging";
import type { Partner } from "@expert/shared-types";
import type { Reservation, Task as TwilioTaskType, Worker } from "twilio-taskrouter";

import { immerable } from "immer";

import type { AgentActivity, Locale, TaskStatus, TaskStatusReason } from "@/sdk";
import type { CaseDetails } from "@/shared-types";

import { Task } from "@/sdk/agent/task";

import type {
    TwilioInboundTaskAttributes,
    TwilioTaskAttributes,
    TwilioVoiceTaskAttributes,
} from "./twilioTaskAttributes";
import type { TaskChannelType } from "./types";

export function getTaskConfig(reservation: Reservation): TwilioTaskConfig {
    const taskConfig: TwilioTaskConfig = {
        taskType: reservation.task.taskChannelUniqueName as TaskChannelType,
        isOnHold: !!reservation.task.transfers.incoming, // TODO: We need more complex treatment here.
        reservation,
    };
    return taskConfig;
}

const TASK_STATUS_MAP: Record<Reservation["status"], TaskStatus> = {
    pending: "pending",
    accepted: "assigned",
    rejected: "cancelled",
    timeout: "cancelled",
    canceled: "cancelled",
    rescinded: "cancelled",
    completed: "completed",
    wrapping: "wrapping",
} as const;

export interface TwilioTaskConfig {
    taskType: TaskChannelType;
    isOnHold: boolean;
    reservation: Reservation;
}

export interface TwilioTaskProps {
    config: TwilioTaskConfig;
    worker: Worker;
    expertIdentity: string;
}

export abstract class TwilioTask<
    TTwilioTaskAttributes extends TwilioTaskType["attributes"] = TwilioTaskType["attributes"],
> extends Task {
    override [immerable] = true;

    public readonly config: TwilioTaskConfig;
    protected readonly worker: Worker;
    protected readonly expertIdentity: string;

    constructor(name: string, { config, worker, expertIdentity }: TwilioTaskProps) {
        super(config.taskType, config.reservation.task.sid, name, config.reservation.dateCreated);
        this.config = config;
        this.worker = worker;
        this.expertIdentity = expertIdentity;
        this.statusReason = config.reservation.task.reason as TaskStatusReason;
        this.attributes = config.reservation.task.attributes as TTwilioTaskAttributes;
        this.setStatus(config);
    }

    public attributes: TTwilioTaskAttributes;

    // --- NOTE: These methods must be implemented by a sub-class ---
    public abstract override get partner(): Partner;

    public abstract override get hasCustomer(): boolean;

    public abstract override get sessionId(): string | undefined;

    public abstract override get previousTaskId(): string | undefined;

    public abstract override readonly onAcceptStatus: AgentActivity;

    public abstract override onAccepted(): void | Promise<void>;

    public abstract override isAgentBusy(agentActivity: AgentActivity): boolean;

    public async accept(logger: Logger) {
        if (this.config.reservation.status === "pending") {
            logger.debug("Accepting Twilio reservation...");
            await this.config.reservation.accept();
            logger.info("Twilio reservation accepted");
        } else {
            logger.debug(
                { status: this.config.reservation.status },
                "Twilio reservation is not pending, skipping accept",
            );
        }
    }

    public get analyticsData(): Record<string, string> {
        return {
            ReservationSid: this.config.reservation.sid,
        };
    }

    // --- ------------------------------------------------------- ---

    // TODO: Nasty way to have session from the very beginning
    public override get sessionIdFallback(): string {
        return (this.config.reservation.task.attributes as TwilioTaskAttributes).sessionIdFallback;
    }

    public static getTaskName(config: TwilioTaskConfig): string {
        return `${config.taskType}-${config.reservation.task.sid}`;
    }

    public override get assignedAt(): Date | undefined {
        return this.config.reservation.status === "accepted" ? this.config.reservation.dateUpdated : undefined;
    }

    public override get caseDetails(): CaseDetails | undefined {
        const attributes = this.config.reservation.task.attributes as TwilioVoiceTaskAttributes;
        return attributes.case_details;
    }

    public override async updateCaseDetails(caseDetailsUpdates: Partial<CaseDetails>): Promise<void> {
        const latestTask = await this.config.reservation.task.fetchLatestVersion();
        const attributes = latestTask.attributes as TwilioVoiceTaskAttributes;
        await this.config.reservation.task.setAttributes({
            ...attributes,
            case_details: {
                ...attributes.case_details,
                ...caseDetailsUpdates,
            },
        });
    }

    private setStatus(config: TwilioTaskConfig) {
        let status = TASK_STATUS_MAP[this.config.reservation.status];

        if (config.reservation.task.status === "wrapping" || config.reservation.task.status === "completed") {
            status = config.reservation.task.status;
        }
        this.status = status;
    }

    public override get locale(): Locale | undefined {
        const attributes = this.config.reservation.task.attributes as TwilioInboundTaskAttributes;
        return attributes.Language as Locale;
    }
}
